<script lang="ts">
	import intlTelInput from 'intl-tel-input';

	import { UiIds } from '../lib/helpers/ui-ids';

	import { createFormStores } from '../lib/validation/create-form-stores';
	import type { Validatable } from '../lib/validation/types';
	import { email } from '../lib/validation/validators/email';
	import { requiredString } from '../lib/validation/validators/required-string';
	import FormHelp from '../lib/forms/FormHelp.svelte';
	import FormError from '../lib/forms/FormError.svelte';
	import { createEventDispatcher, onMount } from 'svelte';
	import PrettyBytes from '../lib/PrettyBytes.svelte';
	import prettyBytes from 'pretty-bytes';
import type { ApplicationFormData } from './types';
	
	export let action: string;
	export let uploadAction: string;
	export let ajaxSubmitURL: string;
	export let jobId: string;
	export let jobTitle: string;
	const dispatch = createEventDispatcher();
	let form: HTMLFormElement;
	const maxSize = 500_000;
	const id = UiIds.create('syntrio-job-application');
	const { data, errors, destroyFormStores, invalid, submitting, touchAll, touched } =
		createFormStores<ApplicationFormData>(
			{
				jobId,
				action,
				uploadAction,
				name: '',
				email: '',
				phone: '',
				resume: null,
				linkedIn: '',
				whySyntrio: ''
			},
			{
				name: [requiredString()],
				email: [requiredString(), email()],
				phone: [
					requiredString(),
					(value) => {
						if (iti) {
							if (!iti.isValidNumber()) {
								return 'Invalid telephone number.';
							}
						}
						return null;
					}
				],
				resume: [],
				linkedIn: [],
				whySyntrio: [requiredString()],
				jobId: [requiredString()],
				uploadAction: [requiredString()],
				action: [requiredString()]
			}
		);
	let telInput: HTMLInputElement;
	let resumeInput: HTMLInputElement;
	let iti: intlTelInput.Plugin;
	const onResumeInputChange = () => {
		$data.resume = resumeInput.files[0] || null;
		$touched.resume = true;
		if (!$data.resume) {
			$errors.resume = 'No file chosen.';
			return;
		}
		if ($data.resume.size > maxSize) {
			$errors.resume = `That file is too large (${prettyBytes($data.resume.size)}.)`;
		}
	};
	onMount(() => {
		iti = intlTelInput(telInput, {
			utilsScript: 'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.13/js/utils.min.js'
		});
	});
	let showErrorPrompt = false;
	const submit = async () => {
		try {
			$submitting = true;
			const fd = new FormData(form);
			fd.append('phoneNormalized', iti.getNumber());
			const response = await fetch(ajaxSubmitURL, {
				method: 'POST',
				body: fd,
				credentials: 'same-origin'
			});
			const result = await response.json();
			if (result.errors) {
				errors.set(result.errors);
				setTimeout(() => {
					$submitting = false;
					showErrorPrompt = true;
					setTimeout(() => showErrorPrompt = false, 5000);
				}, 500)
				
			} else {
				setTimeout(() => {
					dispatch('success', $data);
				}, 500);
				
			}
		} catch (error) {
			$submitting = false;
		}
	};
</script>

<form bind:this={form} on:submit|preventDefault={submit}>
	<input type="hidden" name="action" bind:value={$data.action} />
	<input type="hidden" name="jobId" bind:value={$data.jobId} />
	

	<p class="small text-muted mb-3">
		Fields marked with an asterisk (<span aria-label="Required" class="required-marker">*</span>) are required.
	</p>
	<div class="mb-3">
		<label class="form-label" for={id + 'name'}>
			Full Name
			<span aria-label="Required" class="required-marker">*</span>
		</label>
		<input
			id={id + 'name'}
			type="text"
			name="name"
			autocomplete="name"
			placeholder="Full Name"
			class="form-control"
			class:is-invalid={$touched.name && $errors.name}
			on:blur={() => ($touched.name = true)}
			bind:value={$data.name}
		/>
		<FormError touched={$touched.name} error={$errors.name} ariaDescribesId={id + 'name'} />
	</div>
	<div class="mb-3">
		<label class="form-label" for={id + 'email'}>
			Email
			<span aria-label="Required" class="required-marker">*</span>
		</label>
		<input
			id={id + 'email'}
			type="email"
			name="email"
			autocomplete="email"
			placeholder="you@example.com"
			class="form-control"
			class:is-invalid={$touched.email && $errors.email}
			on:blur={() => ($touched.email = true)}
			bind:value={$data.email}
		/>
		<FormError touched={$touched.email} error={$errors.email} ariaDescribesId={id + 'email'} />
	</div>
	<div class="mb-3">
		<label class="form-label" for={id + 'phone'}>
			Phone
			<span aria-label="Required" class="required-marker">*</span>
		</label>
		<input
			id={id + 'phone'}
			type="tel"
			name="phone"
			autocomplete="tel"
			placeholder="(000) 000-0000"
			class="form-control"
			class:is-invalid={$touched.phone && $errors.phone}
			on:blur={() => ($touched.phone = true)}
			bind:value={$data.phone}
			bind:this={telInput}
		/>
		<FormError touched={$touched.phone} error={$errors.phone} ariaDescribesId={id + 'phone'} />
	</div>
	<div class="mb-3">
		<label for={id + 'resume'} class="form-label">
			Attach Resume
			<span aria-label="Required" class="required-marker">*</span>
		</label>
		<input
			class="form-control"
			name="resume"
			type="file"
			id={id + 'resume'}
			bind:this={resumeInput}
			class:is-invalid={$touched.resume && $errors.resume}
			on:change={onResumeInputChange}
		/>
		<FormError touched={$touched.resume} error={$errors.resume} ariaDescribesId={id + 'resume'} />
		<FormHelp ariaDescribesId={id + 'input'}>
			Max Size:
			<PrettyBytes bytes={maxSize} />
		</FormHelp>
	</div>
	<div class="mb-3">
		<label class="form-label" for={id + 'linkedIn'}>LinkedIn Profile URL</label>
		<input
			id={id + 'linkedIn'}
			type="text"
			name="linkedIn"
			placeholder="https://www.linkedin.com/in/your-profile/"
			class="form-control"
			class:is-invalid={$touched.linkedIn && $errors.linkedIn}
			on:blur={() => ($touched.linkedIn = true)}
			bind:value={$data.linkedIn}
		/>
		<FormError
			touched={$touched.linkedIn}
			error={$errors.linkedIn}
			ariaDescribesId={id + 'linkedIn'}
		/>
		<FormHelp ariaDescribesId={id + 'input'}>Optional.</FormHelp>
	</div>
	<div class="mb-3">
		<label class="form-label" for={id + 'whySyntrio'}>
			Why do you want to work for Syntrio?
			<span aria-label="Required" class="required-marker">*</span>
		</label>
		<textarea
			rows="5"
			id={id + 'whySyntrio'}
			name="whySyntrio"
			class="form-control"
			class:is-invalid={$touched.whySyntrio && $errors.whySyntrio}
			on:blur={() => ($touched.whySyntrio = true)}
			bind:value={$data.whySyntrio}
		/>
		<FormError
			touched={$touched.whySyntrio}
			error={$errors.whySyntrio}
			ariaDescribesId={id + 'whySyntrio'}
		/>
	</div>
	<div class="submit-row mb-3">
		<div>
			{#if $submitting}
			<div class="icon-layout text-muted">
				<div class="spinning">
					<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" class="bi bi-hourglass" viewBox="0 0 16 16">
						<path d="M2 1.5a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-1v1a4.5 4.5 0 0 1-2.557 4.06c-.29.139-.443.377-.443.59v.7c0 .213.154.451.443.59A4.5 4.5 0 0 1 12.5 13v1h1a.5.5 0 0 1 0 1h-11a.5.5 0 1 1 0-1h1v-1a4.5 4.5 0 0 1 2.557-4.06c.29-.139.443-.377.443-.59v-.7c0-.213-.154-.451-.443-.59A4.5 4.5 0 0 1 3.5 3V2h-1a.5.5 0 0 1-.5-.5zm2.5.5v1a3.5 3.5 0 0 0 1.989 3.158c.533.256 1.011.791 1.011 1.491v.702c0 .7-.478 1.235-1.011 1.491A3.5 3.5 0 0 0 4.5 13v1h7v-1a3.5 3.5 0 0 0-1.989-3.158C8.978 9.586 8.5 9.052 8.5 8.351v-.702c0-.7.478-1.235 1.011-1.491A3.5 3.5 0 0 0 11.5 3V2h-7z"/>
					</svg>
				</div>
				<div class="ms-2">
					Please wait...
				</div>
			</div>
			
			{/if}
			{#if showErrorPrompt}
				<div class="icon-layout text-muted">
					<div class="text-danger">
						<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" class="bi bi-cone" viewBox="0 0 16 16">
							<path d="M7.03 1.88c.252-1.01 1.688-1.01 1.94 0l2.905 11.62H14a.5.5 0 0 1 0 1H2a.5.5 0 0 1 0-1h2.125L7.03 1.88z"/>
						</svg>
					</div>
					<div class="ms-2">
						Please correct the error(s).
					</div>
				</div>
			{/if}
		</div>
		<button type="submit" class="btn btn-primary btn-lg" disabled={$invalid || $submitting}>
			Apply
		</button>
	</div>
</form>

<style lang="scss">
	@import '../../node_modules/bootstrap/scss/functions';
	@import '../../node_modules/bootstrap/scss/mixins';
	@import '../../node_modules/bootstrap/scss/variables';
	
	.required-marker {
		font-size: 18px;
		color: $danger;
	}
	:global(.iti) {
		width: 100%;
	}
	.form-label {
		display: block;
		margin-bottom: $spacer/4;
	}
	.form-control {
		display: block;
		width: 100%;
		&.is-invalid {
			border-color: $danger;
		}
	}
	.mb-3 {
		margin-bottom: $spacer;
	}
	.icon-layout {
		display: flex;
		align-items: center;
	}
	.ms-2 {
		margin-right: $spacer;
	}
	.small {
		font-size: $font-size-sm;
	}
	.text-muted {
		color: $text-muted;
	}

	.submit-row {
		display: flex;
		justify-content: space-between;
		align-items: center;
	}

	.btn-primary:disabled {
		opacity: .5;
	}
	

	@keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  .spinning {
    display: inline-block;
    animation-name: spin;
    animation-duration: 1s;
    animation-iteration-count: infinite;
    animation-timing-function: ease-in-out;
  }
</style>
